import { OpenAPI } from 'api/schema/core/OpenAPI'
import { request as __request } from 'api/schema/core/request'
import { Point } from 'geojson'
import { ILot, IStation, IStationControlBoard, IStationControlStatus, IStationSoftwareStatus, IStationStepsStatus, IStationTelemetrySnapshot } from 'utils/interfaces'

import { MainService } from './schema'

function mapStationFromRaw(stationRaw: Record<string, any>): IStation {
  const point =
    stationRaw.point && Array.isArray(stationRaw.point.coordinates)
      ? { type: 'Point' as const, coordinates: stationRaw.point.coordinates }
      : stationRaw.point ?? null
  return {
    id: stationRaw.id,
    station_id: stationRaw.station_id,
    online: Boolean(stationRaw.online),
    name: stationRaw.name || 'Unnamed unit',
    point,
    createdAt: stationRaw.created_at,
    updatedAt: stationRaw.updated_at,
    lastTelemetryAt: stationRaw.last_telemetry_at ?? null,
    description: stationRaw.description || '',
    facility_id: stationRaw.facility_id ?? null,
    reported_lot_ids: Array.isArray(stationRaw.reported_lot_ids) ? stationRaw.reported_lot_ids : [],
    expects_telemetry: stationRaw.expects_telemetry ?? true,
    feedback_status: stationRaw.feedback_status,
    inventory_mismatch: Boolean(stationRaw.inventory_mismatch),
    lot_ids_only_in_db: Array.isArray(stationRaw.lot_ids_only_in_db) ? stationRaw.lot_ids_only_in_db : [],
    lot_ids_only_in_telemetry: Array.isArray(stationRaw.lot_ids_only_in_telemetry)
      ? stationRaw.lot_ids_only_in_telemetry
      : [],
    lot_model_mismatches: Array.isArray(stationRaw.lot_model_mismatches)
      ? stationRaw.lot_model_mismatches
      : [],
    cannot_delete_reason: stationRaw.cannot_delete_reason || null,
  }
}

function mapLotFromRaw(lotsRaw: Record<string, any>): ILot {
  const stateHistory = Array.isArray(lotsRaw.state_history)
    ? lotsRaw.state_history.map((stateRaw: Record<string, any>) => ({
        id: stateRaw.id,
        event_type: stateRaw.event_type ?? null,
        state_status: stateRaw.state_status,
        maintenance_stage: stateRaw.maintenance_stage ?? null,
        occupied_status: stateRaw.occupied_status ?? null,
        error_code: stateRaw.error_code,
        error_msg: stateRaw.error_msg,
        timestamp: stateRaw.timestamp,
        robot_sn: stateRaw.robot_sn,
      }))
    : undefined
  return {
    id: lotsRaw.id,
    name: lotsRaw.name,
    lot_model: lotsRaw.lot_model,
    robot_station: lotsRaw.robot_station,
    type: lotsRaw.type,
    lot_id: lotsRaw.lot_id,
    state_history: stateHistory,
    createdAt: lotsRaw.created_at,
    updatedAt: lotsRaw.updated_at,
    cannot_delete_reason: lotsRaw.cannot_delete_reason || null,
  }
}

export type StationLotHistoryRow = {
  id: number
  event_type: number | null
  state_status: number
  maintenance_stage: number | null
  occupied_status: number | null
  error_code: number | null
  error_msg: string | null
  timestamp: string
  robot_sn: number | string | null
  lot_id: number
  lot_name: string
}

export type StationLotHistoryPage = {
  results: StationLotHistoryRow[]
  total: number
  page_size: number
  page_number: number
}

export const StationApi = {
  fetchStation: async (id: number): Promise<IStation> => {
    const stationRaw = await MainService.mainRobotStationRetrieve(id)
    return mapStationFromRaw(stationRaw as Record<string, any>)
  },
  fetchStations: async (options?: { facilityId?: number }): Promise<IStation[]> => {
    const lotsRaw = await __request<any>(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/',
      query: options?.facilityId ? { facility: options.facilityId, page_size: 500 } : { page_size: 500 },
    })
    if (!lotsRaw.results) {
      return []
    }
    return lotsRaw.results.map((stationRaw: Record<string, any>) => mapStationFromRaw(stationRaw))
  },
  fetchStationsPage: async (
    page: number,
    pageSize: number
  ): Promise<{ results: IStation[]; count: number }> => {
    const raw = await __request<any>(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/',
      query: { page, page_size: pageSize },
    })
    const results = Array.isArray(raw?.results) ? raw.results.map((row: Record<string, any>) => mapStationFromRaw(row)) : []
    return { results, count: Number(raw?.total || raw?.count || results.length) || 0 }
  },

  fetchLotsForStation: async (robotStationId: number): Promise<ILot[]> => {
    const lotsRaw = await __request<any>(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-lot/',
      query: {
        robot_station: robotStationId,
        page_size: 200,
        ordering: 'lot_id',
      },
    })
    if (!lotsRaw.results) {
      return []
    }
    return lotsRaw.results.map(mapLotFromRaw)
  },

  fetchLots: async (): Promise<ILot[]> => {
    const lotsRaw = await MainService.mainRobotLotList(undefined, undefined, 500)
    if (!lotsRaw.results) {
      return []
    }
    return lotsRaw.results.map(lot => mapLotFromRaw(lot as Record<string, any>))
  },

  fetchTelemetrySnapshot: async (stationId: number): Promise<IStationTelemetrySnapshot> => {
    return __request<IStationTelemetrySnapshot>(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/{id}/telemetry-snapshot/',
      path: { id: stationId },
    })
  },

  /** Station software Current/Available (MR-SW-STATION). */
  fetchSoftware: async (
    stationId: number,
    options?: { refresh?: boolean }
  ): Promise<IStationSoftwareStatus> => {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/{id}/software/',
      path: { id: stationId },
      query: options?.refresh ? { refresh: '1' } : undefined,
    })
  },

  /** Trigger station OTA (MR-SW-STATION Phase B). Fire-and-forget GitLab deploy. */
  requestSoftwareUpdate: async (
    stationId: number,
    options?: { target_version?: string }
  ): Promise<{
    accepted: boolean
    target_tag: string
    target_version?: string | null
    environment: string
    pipeline_id?: number | null
    pipeline_web_url?: string | null
    spinner_timeout_sec?: number | null
  }> => {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-station/{id}/software/update/',
      path: { id: stationId },
      body: options?.target_version ? { target_version: options.target_version } : {},
    })
  },

  /** FMS-STEPS status (session + command catalog). */
  fetchSteps: async (stationId: number): Promise<IStationStepsStatus> => {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/{id}/steps/',
      path: { id: stationId },
    })
  },

  stepsOccupy: async (stationId: number, lotId: number) => {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-station/{id}/steps/occupy/',
      path: { id: stationId },
      body: { lot_id: lotId },
    })
  },

  stepsRelease: async (stationId: number) => {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-station/{id}/steps/release/',
      path: { id: stationId },
      body: {},
    })
  },

  stepsCommand: async (
    stationId: number,
    command: number,
    options?: { confirm_dangerous?: boolean }
  ) => {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-station/{id}/steps/command/',
      path: { id: stationId },
      body: {
        command,
        confirm_dangerous: Boolean(options?.confirm_dangerous),
      },
    })
  },

  /** FMS-CTRL.1 status (FORCE_CANCEL / FORCE_RELEASE / CLEAR_OVERPRESSURE). */
  fetchControl: async (stationId: number): Promise<IStationControlStatus> => {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/{id}/control/',
      path: { id: stationId },
    })
  },

  /** Unified Control tab board (STEPS + CTRL + software, one snapshot). */
  fetchControlBoard: async (stationId: number): Promise<IStationControlBoard> => {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/{id}/control-board/',
      path: { id: stationId },
    })
  },

  controlAction: async (
    stationId: number,
    body: { action: string; lot_id: number; target?: string }
  ) => {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-station/{id}/control/action/',
      path: { id: stationId },
      body,
    })
  },

  /** Batch telemetry snapshots for Assets list (MR-04d-perf + inventory/feedback). */
  fetchTelemetrySnapshots: async (
    stationIds: number[]
  ): Promise<
    Array<{
      id: number
      station_id: string
      last_telemetry_at: string | null
      online: boolean
      feedback_status?: IStation['feedback_status']
      expects_telemetry?: boolean
      inventory_mismatch?: boolean
      lot_ids_only_in_db?: number[]
      lot_ids_only_in_telemetry?: number[]
      reported_lot_ids?: number[]
      lot_model_mismatches?: IStation['lot_model_mismatches']
      snapshot: IStationTelemetrySnapshot['snapshot']
    }>
  > => {
    if (stationIds.length === 0) {
      return []
    }
    const response = await __request<{ results?: any[] }>(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/telemetry-snapshots/',
      query: { ids: stationIds.join(',') },
    })
    return response.results ?? []
  },

  /** Paginated station lot history for History tab (MR-04d-perf). */
  fetchLotHistory: async (
    stationId: number,
    page = 1,
    pageSize = 10
  ): Promise<StationLotHistoryPage> => {
    const response = await __request<any>(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/{id}/lot-history/',
      path: { id: stationId },
      query: { page, page_size: pageSize },
    })
    return {
      results: response.results ?? [],
      total: Number(response.total ?? 0),
      page_size: Number(response.page_size ?? pageSize),
      page_number: Number(response.page_number ?? page),
    }
  },

  createStation: async (
    name: string,
    station_id: string,
    point: Point,
    facility_id?: number | null
  ): Promise<IStation> => {
    const stationRaw = await __request<Record<string, any>>(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-station/',
      mediaType: 'application/json',
      body: {
        name,
        station_id,
        point,
        ...(facility_id != null ? { facility_id } : {}),
      },
    })
    return mapStationFromRaw({
      ...stationRaw,
      facility_id: stationRaw.facility_id ?? facility_id ?? null,
    })
  },

  createLot: async (params: {
    name: string
    lot_id: number
    lot_model: string
    robot_station_id: number
  }): Promise<ILot> => {
    const lotRaw = await __request<Record<string, any>>(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-lot/',
      mediaType: 'application/json',
      body: {
        name: params.name,
        lot_id: params.lot_id,
        lot_model: params.lot_model,
        robot_station_id: params.robot_station_id,
      },
    })
    return mapLotFromRaw(lotRaw)
  },
  patchStation: async (
    id: number,
    name: string,
    station_id: string,
    point: Point,
    facility_id?: number | null
  ): Promise<IStation> => {
    const stationRaw = await __request<Record<string, any>>(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/robot-station/{id}/',
      path: { id },
      body: {
        name,
        station_id,
        point,
        ...(facility_id !== undefined ? { facility_id } : {}),
      },
    })
    return mapStationFromRaw(stationRaw)
  },
  deleteStation: async (id: number): Promise<void> => {
    await MainService.mainRobotStationDestroy(id)
  },
}
